type FontWeight = number;

// 可以使用字符串类型来表示盒阴影
type ShadowElevation = string;

type FontSize = number;

type LineHeight = number;

type ZIndex = number;

// 用字符串表示颜色，通常是hex值或rgba值
type ColorStr = string;

export type Token = {
  // 字重
  'font-weight-regular'?: FontWeight;
  'font-weight-medium'?: FontWeight;
  'font-weight-bold'?: FontWeight;

  // 阴影大小
  'shadow-elevation-small'?: ShadowElevation;
  'shadow-elevation-medium'?: ShadowElevation;
  'shadow-elevation-large'?: ShadowElevation;

  // 字号
  'font-size-super-large'?: FontSize;
  'font-size-extra-large'?: FontSize;
  'font-size-large'?: FontSize;
  'font-size-base'?: FontSize;
  'font-size-medium'?: FontSize;
  'font-size-small'?: FontSize;
  'font-size-extra-small'?: FontSize;
  'font-size-super-small'?: FontSize;
  'font-size-xxs'?: FontSize;
  'font-size-xxxs'?: FontSize;

  // 行高
  'line-height-super-large'?: LineHeight;
  'line-height-extra-large'?: LineHeight;
  'line-height-large'?: LineHeight;
  'line-height-base'?: LineHeight;
  'line-height-medium'?: LineHeight;
  'line-height-small'?: LineHeight;
  'line-height-extra-small'?: LineHeight;
  'line-height-super-small'?: LineHeight;
  'line-height-xxs'?: LineHeight;

  // 层级
  'z-index-axisLabel'?: ZIndex;
  'z-index-bar'?: ZIndex;
  'z-index-line'?: ZIndex;
  'z-index-splitLine'?: ZIndex;
  'z-index-axisPointer'?: ZIndex;
  'z-index-data-item'?: ZIndex;
  'z-index-marker'?: ZIndex;

  // 颜色 默认主色
  'color-visualization-primary'?: ColorStr;
  'color-visualization-01'?: ColorStr;
  'color-visualization-02'?: ColorStr;
  'color-visualization-03'?: ColorStr;
  'color-visualization-04'?: ColorStr;
  'color-visualization-05'?: ColorStr;
  'color-visualization-06'?: ColorStr;
  'color-visualization-07'?: ColorStr;
  'color-visualization-08'?: ColorStr;
  'color-visualization-09'?: ColorStr;
  // 颜色 分割线颜色
  'color-visualization-divider'?: ColorStr;
  // 颜色 tooltip背景和axisPointer线的颜色
  'color-visualization-tooltip'?: ColorStr;
  // 颜色 axisPointer标签背景色和线类型的颜色、阴影类型的填充色
  'color-visualization-highlight-background-tick'?: ColorStr;
  'color-visualization-highlight-line'?: ColorStr;
  'color-visualization-highlight-block'?: ColorStr;
  // 颜色 弱化背景色
  'color-background-weak'?: ColorStr;
  // 颜色 datazoom数据背景中未选中的颜色
  'color-visualization-datazoom-data-bg': ColorStr;
  // 颜色 datazoom数据背景中已选中的颜色
  'color-visualization-datazoom-data-select': ColorStr;
  // 颜色 datazoom背景中已选中的框的颜色
  'color-visualization-datazoom-data-filler': ColorStr;

  // 颜色 涨跌色
  'color-price-up'?: ColorStr;
  'color-price-down'?: ColorStr;
  'color-price-even'?: ColorStr;

  // 交易业务-下跌
  'color-deal-price-down'?: ColorStr;

  // 下跌-色弱模式
  'color-price-down-weakness'?: ColorStr;

  // 文字/图标颜色
  // 白版
  'color-text-primary'?: ColorStr;
  'color-text-secondary'?: ColorStr;
  'color-text-tertiary'?: ColorStr;
  'color-text-quanternary'?: ColorStr;

  // 反色
  'color-text-inverse'?: ColorStr;
  'color-text-inverse-primary'?: ColorStr;
  'color-text-inverse-secondary'?: ColorStr;
  'color-text-inverse-tertiary'?: ColorStr;
  'color-text-inverse-quanternary'?: ColorStr;

  // 文字链接色
  'color-text-link'?: ColorStr;

  [token: string]: unknown;
};

export type OptionToken = { [key: string]: unknown };

/**
 * UI稿地址
 * https://frontend.myhexin.com/design-resource/access?jira=UED-9356&type=UI
 * key为UI稿上token名称去掉$，转为驼峰的形式
 * value为该token影响的主题配置
 */
const tokenMap = {
  'font-weight-bold': ['xAxis.axisPointer.label.fontWeight', 'yAxis.axisPointer.label.fontWeight'],
  'font-weight-regular': [],
  'font-weight-medium': [
    'xAxis.nameTextStyle.fontWeight',
    'yAxis.nameTextStyle.fontWeight',
    'bar.label.fontWeight',
    'line.label.fontWeight'
  ],
  'font-size-xxs': [
    'xAxis.nameTextStyle.fontSize',
    'xAxis.axisLabel.fontSize',
    'yAxis.nameTextStyle.fontSize',
    'yAxis.axisLabel.fontSize',
    'xAxis.axisPointer.label.fontSize',
    'yAxis.axisPointer.label.fontSize',
    'bar.label.fontSize',
    'line.label.fontSize'
  ],
  // FIXME 图元属性--行高和字号之间会有连带效应：计算文本的包围盒信息过程中，行高的优先级高于字号
  // 如果在token中固定行高，那么在用户没有显式声明行高时，文本包围盒会不准确
  // 因此暂定token中不预设行高，默认跟随字号大小变化
  // 'line-height-super-small': [
  //   'xAxis.nameTextStyle.lineHeight',
  //   'xAxis.axisLabel.lineHeight',
  //   'yAxis.nameTextStyle.lineHeight',
  //   'yAxis.axisLabel.lineHeight',
  //   'xAxis.axisPointer.label.lineHeight',
  //   'yAxis.axisPointer.label.lineHeight',
  //   'bar.label.lineHeight',
  //   'line.label.lineHeight'
  // ],
  // 颜色 axisPointer文本颜色和文本背景色
  'color-text-inverse': ['xAxis.axisPointer.label.color', 'yAxis.axisPointer.label.color'],
  'color-visualization-highlight-line': [
    'xAxis.axisPointer.lineStyle.color',
    'yAxis.axisPointer.lineStyle.color'
  ],
  'color-visualization-highlight-block': [
    'xAxis.axisPointer.shadowStyle.color',
    'yAxis.axisPointer.shadowStyle.color'
  ],
  'color-visualization-highlight-background-tick': [
    'xAxis.axisPointer.label.backgroundColor',
    'yAxis.axisPointer.label.backgroundColor'
  ],
  'color-text-secondary': [
    'xAxis.nameTextStyle.color',
    'xAxis.axisLabel.color',
    'yAxis.nameTextStyle.color',
    'yAxis.axisLabel.color',
    'bar.label.color',
    'line.label.color'
  ],
  'color-visualization-divider': [
    'xAxis.splitLine.lineStyle.color',
    'yAxis.splitLine.lineStyle.color'
  ],
  'color-visualization-divider-deep': [
    'xAxis.axisLine.lineStyle.color',
    'yAxis.axisLine.lineStyle.color'
  ],
  'color-visualization-tooltip': [
    'xAxis.axisPointer.lineStyle.color',
    'yAxis.axisPointer.lineStyle.color'
  ],
  'color-background-weak': ['dataZoom.backgroundColor'],
  'z-index-axisLabel': ['yAxis.axisLabel.dvZ', 'dvWaterMark.z'],
  'z-index-axisPointer': ['xAxis.axisPointer.z', 'yAxis.axisPointer.z'],
  'z-index-splitLine': ['xAxis.splitLine.dvZ', 'yAxis.splitLine.dvZ'],
  // TODO 确定marker、数据点、series的层级需要修改哪些配置项
  'z-index-marker': [],
  'z-index-data-item': [],
  'z-index-bar': ['bar.z'],
  'z-index-line': ['line.z'],
  'color-visualization-datazoom-data-bg': ['dataZoom.dataBackground.areaStyle.color'],
  'color-visualization-datazoom-data-filler': ['dataZoom.fillerColor'],
  'color-visualization-datazoom-data-select': ['dataZoom.selectedDataBackground.areaStyle.color']
};

function setAttribute(target: OptionToken, pathArr: string[], value: any) {
  let obj = target;
  pathArr.forEach((key, keyIndex) => {
    if (keyIndex === pathArr.length - 1) {
      obj[key] = value;
      return;
    }

    if (obj[key] == null) {
      obj[key] = {};
    }

    obj = obj[key];
  });
}

export function parseToken(tokens: Token) {
  const result = {} as OptionToken;

  Object.keys(tokens).forEach(key => {
    const value = tokens[key];
    tokenMap[key]?.forEach((item: string) => {
      setAttribute(result, item.split('.'), value);
    });
    result[key] = value;
  });

  return result;
}
